import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { login } from '../../api';
import { LoginResponseData, UserCacheData } from '../../types/auth';
import { getLanguageByIP } from '../../utils/geoLanguage';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { resolveAndApplyUserAppConfig } from '../../utils/userAppConfig';
import { useDataCache } from '../../contexts/BreakdownDataCacheContext';
import { SESSION_HOME_BOOTSTRAP_KEY } from '../../contexts/FilmContext';
import {
  clearRememberedCredentials,
  loadRememberedCredentials,
  saveRememberedCredentials,
} from '../../utils/rememberedCredentials';
import WindowControls from '../../components/WindowControls';
import DropdownMenu from '../../components/DropdownMenu';
import { APP_LANGUAGE_OPTIONS } from '../../i18n/languageOptions';
import './Login.css';

// 检查是否在Electron环境中
const isElectron = typeof window !== 'undefined' && window.require && window.require('electron');

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith('en') ?? false;
  const { setUserCache, clearUserCache } = useCurrentUser();
  const { setTheme } = useThemeContext();
  const { clearAllCache: clearBreakdownDataCache } = useDataCache();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberPassword, setRememberPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  
  // 创建 ref 用于用户名输入框
  const usernameInputRef = useRef<HTMLInputElement>(null);
  const languageButtonRef = useRef<HTMLDivElement>(null);
  const [languageMenuVisible, setLanguageMenuVisible] = useState(false);
  const [languageMenuPosition, setLanguageMenuPosition] = useState({ top: 0, left: 0 });

  const getLanguageName = (code: string): string => {
    const nameMap: Record<string, string> = {
      'zh-CN': '中文简体', 'zh-HK': '中文繁体', 'en': 'English'
    };
    return nameMap[code] || '中文简体';
  };
  const languageOptions = APP_LANGUAGE_OPTIONS;
  const rawLang = i18n.language || 'zh-CN';
  const currentLanguage = rawLang.startsWith('zh-HK') ? 'zh-HK' : rawLang.startsWith('zh') ? 'zh-CN' : 'en';

  useEffect(() => {
    if (languageMenuVisible && languageButtonRef.current) {
      const rect = languageButtonRef.current.getBoundingClientRect();
      setLanguageMenuPosition({ top: rect.bottom + 4, left: rect.left });
    }
  }, [languageMenuVisible]);

  // 组件挂载时自动聚焦到用户名输入框
  useEffect(() => {
    let cancelled = false;
    void loadRememberedCredentials().then((credentials) => {
      if (cancelled) return;
      if (credentials) {
        setIdentifier(credentials.identifier);
        setPassword(credentials.password);
        setRememberPassword(true);
      } else {
        usernameInputRef.current?.focus();
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // 无本地语言缓存时，根据 IP 推断默认语言
  useEffect(() => {
    const cached = localStorage.getItem('i18nextLng');
    if (cached) return;
    getLanguageByIP().then((lang) => {
      i18n.changeLanguage(lang);
    });
  }, [i18n]);

  // Electron 下根据语言调整登录窗口宽度（英文时 login-box 更宽）
  useEffect(() => {
    if (!isElectron) return;
    try {
      const { ipcRenderer } = window.require('electron');
      const width = isEnglish ? 520 : 420;
      const height = 600;
      ipcRenderer.send('resize-login-window', { width, height });
    } catch {
      // 非 Electron 或 require 失败时忽略
    }
  }, [isEnglish]);

  const handleAgreementLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/privacy-policy');
  };

  const handleRegisterClick = () => {
    // 构建注册页面的 URL
    // 使用 window.location.origin 自动适配当前端口（无论是 9101 还是 9103）
    const baseUrl = window.location.origin;
    const signupUrl = `${baseUrl}/#/signup`;
    
    if (isElectron) {
      try {
        const { shell } = window.require('electron');
        shell.openExternal(signupUrl);
      } catch (err) {
        console.warn('Failed to open signup URL:', err);
        // 如果 Electron 打开失败，回退到 window.open
        window.open(signupUrl, '_blank', 'noopener,noreferrer');
      }
    } else {
      window.open(signupUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAgreeToTerms(true);
    setError('');

    // 表单验证
    if (!identifier && !password) {
      setError(t('auth.pleaseEnterUsernameAndPassword'));
      return;
    }
    
    if (!identifier) {
      setError(t('auth.pleaseEnterUsername'));
      return;
    }
    
    if (!password) {
      setError(t('auth.pleaseEnterPassword'));
      return;
    }

    setLoading(true);

    try {
      // 清空旧缓存（场景数据缓存在 Router 外层，需显式清空）
      clearUserCache();
      clearBreakdownDataCache();

      const response = await login({ identifier, password });
      
      // 检查响应数据是否存在
      if (!response.data) {
        throw new Error(t('auth.loginFailed'));
      }

      // 根据实际响应格式获取数据
      // 如果是标准 ResponseUtil 格式，使用 response.data.data
      // 如果是直接返回数据格式，使用 response.data
      let loginData: LoginResponseData;
      if (response.data.success !== undefined && response.data.data) {
        // 标准 ResponseUtil 格式
        if (!response.data.success) {
          throw new Error(response.data.message || t('auth.loginFailed'));
        }
        loginData = response.data.data;
      } else if ('access_token' in response.data) {
        // 直接返回登录数据格式，需要类型断言
        loginData = response.data as unknown as LoginResponseData;
      } else {
        throw new Error(t('auth.loginFailed'));
      }
      
      const { access_token, user, organization, departments } = loginData;
      
      // 构造新的缓存数据结构
      const cacheData: UserCacheData = {
        user,
        organization: organization || undefined,
        departments: departments || [],
        accessToken: access_token,
        cacheTime: new Date().toISOString()
      };
      
      // 使用 CurrentUserContext 更新用户缓存（同时更新 React 状态和 localStorage）
      setUserCache(cacheData);

      if (rememberPassword) {
        try {
          await saveRememberedCredentials({ identifier, password });
        } catch (credentialError) {
          console.warn('安全保存登录凭据失败:', credentialError);
        }
      } else {
        clearRememberedCredentials();
      }

      resolveAndApplyUserAppConfig(user, i18n.language, { i18n, setTheme });

      // 行为记忆由 CurrentUserContext 在 setUserCache 后根据 user 自动 hydrate

      // 登录成功后清空tabbar，只保留主页标签
      localStorage.setItem('tabs', JSON.stringify([
        { id: 'home', title: t('common.home'), active: true, fixed: true, path: '/' }
      ]));

      // 通知electron主进程登录成功（仅在Electron环境中）
      if (isElectron) {
        try {
          const { ipcRenderer } = window.require('electron');
          ipcRenderer.send('login-success');
        } catch (error) {
          console.log('Failed to send login-success event:', error);
        }
      }
      
      sessionStorage.setItem(SESSION_HOME_BOOTSTRAP_KEY, '1');
      navigate('/', { state: { fromLogin: true } });
    } catch (error: any) {
      console.error('Login error:', error);
      
      let errorMessage = t('auth.loginFailed');
      if (error.code === 'ECONNABORTED') {
        errorMessage = t('common.error');
      } else if (error.response) {
        switch (error.response.status) {
          case 401:
            errorMessage = t('auth.invalidCredentials');
            break;
          case 500:
            errorMessage = t('common.error');
            break;
          default:
            errorMessage = error.response.data?.message || t('auth.loginFailed');
        }
      } else if (error.request) {
        errorMessage = t('common.error');
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <WindowControls />
      <div className="login-content">
        <div className="login-logo">
          <img src={require('../../assets/images/logo_horizontal.png')} alt="LINEUP" />
        </div>
        <div className={`login-box${isEnglish ? ' login-box--en' : ''}`}>
          <div
            ref={languageButtonRef}
            className="login-lang-btn"
            onClick={() => setLanguageMenuVisible(!languageMenuVisible)}
            title={getLanguageName(currentLanguage)}
          >
            <span className="login-lang-name">{getLanguageName(currentLanguage)}</span>
          </div>
          <DropdownMenu
            items={languageOptions}
            visible={languageMenuVisible}
            onClose={() => setLanguageMenuVisible(false)}
            onSelect={(item) => {
              i18n.changeLanguage(item.id as string);
              setLanguageMenuVisible(false);
            }}
            position={languageMenuPosition}
            useFixedPosition={true}
            selectedValue={currentLanguage}
            width={languageButtonRef.current?.offsetWidth}
          />
          <form onSubmit={handleLogin}>
            <div className="input-group">
              <input
                type="text"
                placeholder={t('auth.username')} 
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                disabled={loading}
                ref={usernameInputRef}
              />
            </div>
            <div className="input-group">
              <input
                type="password"
                placeholder={t('auth.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                autoComplete="current-password"
              />
            </div>
            <div className="error-message-container">{error || ''}</div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? t('common.loading') : t('auth.login')}
            </button>
            <div className="login-actions-row">
              <label className="login-checkbox-label">
                <input
                  type="checkbox"
                  checked={rememberPassword}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setRememberPassword(checked);
                    if (!checked) clearRememberedCredentials();
                  }}
                  disabled={loading}
                  className="login-checkbox"
                />
                <span>{t('auth.rememberPassword')}</span>
              </label>
              <div className="login-actions-right">
                <button type="button" className="login-link-button" onClick={handleRegisterClick}>
                  {t('auth.register')}
                </button>
                <button type="button" className="login-link-button" onClick={() => navigate('/forgot-password')}>
                  {t('auth.forgotPassword')}
                </button>
              </div>
            </div>
            <div className="login-agreement-row">
              <label className="login-checkbox-label">
                <input
                  type="checkbox"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  disabled={loading}
                  className="login-checkbox"
                />
                <span>{t('auth.agreementPrefix')}<a href="#/privacy-policy" className="login-agreement-link" onClick={handleAgreementLinkClick}>{t('auth.agreementLinkText')}</a>{t('auth.agreementSuffix')}</span>
              </label>
              <p className="login-agreement-hint">{t('auth.agreementTipLogin')}</p>
            </div>
          </form>
        </div>
      </div>
      <div className="login-footer">
        <button type="button" className="login-footer-link">{t('auth.aboutLineup')}</button>
        <span className="login-footer-sep">|</span>
        <button type="button" className="login-footer-link" onClick={() => navigate('/privacy-policy')}>{t('auth.userAgreement')}</button>
        <span className="login-footer-sep">|</span>
        <button type="button" className="login-footer-link" onClick={() => navigate('/privacy-policy')}>{t('auth.privacyPolicy')}</button>
        <span className="login-footer-sep">|</span>
        <button type="button" className="login-footer-link">{t('auth.softwareUpdate')}</button>
        <span className="login-footer-sep">|</span>
        <button type="button" className="login-footer-link">{t('auth.help')}</button>
      </div>
    </div>
  );
};

export default Login;
